import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderKanban, Plus } from 'lucide-react';
import type { ProjectSummary } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useProjects } from '../../db/tasks';
import { useUi } from '../../state/ui';
import { cn } from '../../lib/cn';
import { dueTone, formatDue, PRIORITY_ICON } from './format';
import { ModuleIcon } from '../ModuleIcon';
import { PageIcon } from '../PageIcon';

export function ProgressBar({ value, label }: { value: number; label: string }) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      className="h-1.5 w-full overflow-hidden rounded-full bg-bg-active"
    >
      <div
        className="h-full rounded-full bg-accent-bright"
        style={{ width: `${Math.round(value * 100)}%` }}
      />
    </div>
  );
}

const isClosed = (p: ProjectSummary) =>
  /^(done|klaar|afgerond|opgelost|gesloten|closed|archived|gearchiveerd)$/i.test(p.status ?? '');

export function ProjectsView() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const data = useProjects();
  const [showClosed, setShowClosed] = useState(false);
  const projects = (data?.projects ?? [])
    .filter((p) => showClosed || !isClosed(p))
    .sort(
      (a, b) =>
        (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999') ||
        a.page.title.localeCompare(b.page.title),
    );

  const create = async () => {
    const page = await store.createPage({ title: '', icon: '🚀' });
    await store.setProp(page.id, 'type', 'project');
    await store.setProp(page.id, 'status', 'lopend');
    open({ kind: 'page', pageId: page.id });
  };

  return (
    <section className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10">
      <div className="flex items-center gap-3">
        <h1 className="flex-1 flex items-center gap-3 text-3xl font-bold tracking-tight">
          <ModuleIcon kind="projects" size="lg" />
          {t('projects.title')}
        </h1>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          {t('projects.showClosed')}
        </label>
        <button
          type="button"
          onClick={() => void create()}
          className="flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg"
        >
          <Plus className="size-4" />
          {t('projects.new')}
        </button>
      </div>
      {data && projects.length === 0 && <p className="mt-8 text-fg-muted">{t('projects.empty')}</p>}
      <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => (
          <li key={p.page.id}>
            <button
              type="button"
              onClick={(e) => open({ kind: 'page', pageId: p.page.id }, e.ctrlKey || e.metaKey)}
              className="flex h-full w-full flex-col gap-2 rounded-xl border border-border p-4 text-left hover:border-accent"
            >
              <span className="flex items-center gap-2 font-semibold">
                <span aria-hidden>
                  <PageIcon
                    icon={p.page.icon}
                    fallback={<FolderKanban className="size-4 text-fg-muted" />}
                  />
                </span>
                <span className="truncate">{p.page.title || t('page.untitled')}</span>
                {p.priority && (
                  <span title={t(`tasks.priorities.${p.priority}`)}>
                    {PRIORITY_ICON[p.priority]}
                  </span>
                )}
              </span>
              <span className="flex flex-wrap gap-2 text-xs text-fg-muted">
                {p.status && <span className="rounded bg-bg-subtle px-1.5 py-0.5">{p.status}</span>}
                {p.deadline && (
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5',
                      dueTone(p.deadline) === 'overdue' && !isClosed(p)
                        ? 'bg-red-500/15 text-danger'
                        : 'bg-bg-subtle',
                    )}
                  >
                    {t('projects.deadline')}: {formatDue(p.deadline, i18n.language, t)}
                  </span>
                )}
                {p.overdue > 0 && (
                  <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-danger">
                    {t('projects.overdue', { count: p.overdue })}
                  </span>
                )}
              </span>
              <span className="mt-auto">
                <ProgressBar
                  value={p.progress}
                  label={t('projects.progressOf', { title: p.page.title })}
                />
                <span className="mt-1 block text-xs text-fg-muted">
                  {t('projects.progress', {
                    done: p.done,
                    total: p.done + p.open,
                    pct: Math.round(p.progress * 100),
                  })}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
